import { ArrowSquareOutIcon, QuestionIcon, TrashSimpleIcon, XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { createFormHook, createFormHookContexts, useSelector } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useMemo, useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'
import { CopyButton } from '../../../components/buttons/copy-button.tsx'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import { GhostButton } from '../../../components/buttons/ghost-button.tsx'
import { Disclosure } from '../../../components/disclosure.tsx'
import * as footerStyles from '../../../components/drawers/floating-footer.css.ts'
import { FloatingFooter } from '../../../components/drawers/floating-footer.tsx'
import { CopyableTextField } from '../../../components/input/copyable-text-field.tsx'
import { RadioGroup, RadioItem } from '../../../components/input/radio.tsx'
import { TextInput } from '../../../components/input/text-input.tsx'
import { TooltipWithHover } from '../../../components/tooltip.tsx'
import { useShowMutationError } from '../../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../../services/toast-service.tsx'
import { usePutIntegration, type PutIntegrationVariables } from '../../../utils/api-client/api-hooks.ts'
import type { AppScanIntegration, Integration, WritableIntegrationType } from '../../../utils/api-client/api-schemas.ts'
import { getIsIntegrationTypeSupportedByApi } from '../../../utils/api-client/api-schemas.ts'
import { buildPath } from '../../../utils/build-path.ts'
import { DeleteIntegrationModal } from './delete-integration-modal.tsx'
import { DocumentationDrawer } from './documentation-drawer.tsx'
import { HealthDot } from './instance-card.tsx'
import * as styles from './integration-drawer.css.ts'
import { FormErrorBanner, IntegrationFieldInput, getFieldFormatError } from './integration-field-input.tsx'
import { IntegrationBrandIcon } from './integration-icon.tsx'
import type {
  CreatableIntegrationType,
  IntegrationField,
  SecretGroup,
  WebhookIntegrationType,
} from './integration-instance-utils.ts'
import {
  ADVANCED_SECTION_SUMMARY,
  TOOL_FIELD_CONFIG,
  UNCHANGED_SELECT_OPTION,
  UNCHANGED_SELECT_VALUE,
  getCatalogEntryForType,
  getDeleteBlockedReason,
  getFixedEndpointReason,
  getHasConnectionWebhookRoute,
  getHasEndpointMoved,
  getHasIntegrationSetupForm,
  getHasWebhookHandoff,
  getInstanceFailureDetail,
  getInstanceHealth,
  getInstanceName,
  getIsFieldRequired,
  getIsHttpUrl,
  getProviderLabel,
  getSecretKeysRequiredForMove,
  getSentFieldValue,
  parseWorkItemIdUpdate,
  toCheckmarxRegionOrUndefined,
} from './integration-instance-utils.ts'
import {
  NAME_FIELD_KEY,
  getFieldErrorText,
  getIntegrationSaveError,
  getIsConflictReportedOnField,
  getIsEndpointFixedRefusal,
  getIsFieldAnchored,
  type IntegrationSaveError,
} from './integration-validation.ts'
import {
  UNPROTECTED_WEBHOOK_HELPER_TEXT,
  WEBHOOK_URL_COPY_LABEL,
  WEBHOOK_URL_LABEL,
  getWebhookAuthMethod,
  getWebhookUrl,
  getWebhookUrlHelperText,
} from './integration-webhook.ts'

// #region Types

type WebhookAuthMethod = 'unchanged' | 'secret' | 'basic'

// Field state lives in the TanStack Form store (not in a component that renders Dialog.Content) so
// typing never re-renders Dialog.Content — that would re-run Radix's FocusScope ref callback and
// exceed React's update-depth limit.
/** Exactly the three keys the webhook fieldset renders, so a typo is a build error, not an empty read. */
type WebhookValues = { webhook_secret: string; webhook_user: string; webhook_password: string }

type ManageFormValues = {
  name: string
  values: Record<string, string>
  webhookMethod: WebhookAuthMethod
  webhookValues: WebhookValues
  saveError: IntegrationSaveError | undefined
}

type ManageDrawerProps = {
  integration: Integration
  onClose: () => unknown
}

// #endregion

// #region Form hook

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

const FORM_DEFAULT_VALUES: ManageFormValues = {
  name: '',
  values: {},
  webhookMethod: 'unchanged',
  webhookValues: { webhook_secret: '', webhook_user: '', webhook_password: '' },
  saveError: undefined,
}

const useManageForm = (defaultValueOverrides: Partial<ManageFormValues> = {}) =>
  useAppForm({ defaultValues: { ...FORM_DEFAULT_VALUES, ...defaultValueOverrides } })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

// #endregion

// #region Helpers

const MOVED_ENDPOINT_PLACEHOLDER = 'Required — the address changed'
const ROTATE_PLACEHOLDER = 'Re-enter to rotate'

const MOVED_ENDPOINT_HELPER_TEXT =
  'Changing where this instance connects requires re-entering all credentials, so Pixee only tests the new address with credentials you supplied.'

/**
 * Reads a non-secret field's stored value off an integration response so it can prefill the edit
 * field, or `undefined` when the response says nothing about that field. Credentials are write-only
 * and never returned, so this only ever reads values like the base URI. Every Phase 2 tool now
 * reports its own non-secret fields, and because this read is keyed off `TOOL_FIELD_CONFIG` rather
 * than a per-tool list, each one prefills with no change here: Sonar's organization and GitHub App
 * name, Checkmarx's region and tenant, Veracode's signing target.
 *
 * AppScan's `webhook_auth_method` and `webhook_user` are the exception, and the reason is that they
 * are not `TOOL_FIELD_CONFIG` entries — the drawer renders them through a rotation chooser instead.
 * `getStoredWebhookAuthSummary` reads those two directly.
 *
 * The absent case is kept distinct from an empty string because two rules turn on it: a required
 * field nobody reported is not something the user cleared, and a select nobody reported opens on
 * "Leave unchanged" rather than on an option Pixee cannot vouch for.
 *
 * A number is read as its text because every field here renders as text and is compared as text:
 * Azure's `work_item_id` is the only one, and discarding it as a non-string is what used to leave it
 * reading as unreported even once the response echoed it — which makes clearing it indistinguishable
 * from leaving it alone, so the `null` that erases the reference never gets sent.
 */
const getStoredValue = (integration: Integration, key: string): string | undefined => {
  const stored: unknown = Reflect.get(integration, key)
  if (typeof stored === 'number') {
    return String(stored)
  }
  return typeof stored === 'string' ? stored : undefined
}

/**
 * The instance's own address, when it is one this page may render as a link.
 *
 * Held to the same scheme check the Setup form applies, rather than trusted for having come back off
 * a response. Neither side of the contract turns a `javascript:` or `data:` URI away on its own —
 * the field is declared `format: uri` and the platform's check is `java.net.URI.isAbsolute()`, which
 * both satisfy — and a `system` instance is configured in the admin console, so it never passed
 * through this product's form at all. `getIsHttpUrl` exists for exactly this: its own doc calls out
 * a scheme being "stored and later rendered as a link" as the thing it prevents.
 */
const getLinkableToolUrl = (integration: Integration): string | undefined => {
  const baseUri = 'base_uri' in integration ? integration.base_uri : undefined
  return baseUri !== undefined && getIsHttpUrl(baseUri) ? baseUri : undefined
}

// A pasted credential routinely carries trailing whitespace, which fails auth with nothing to point at.
const secretOrUndefined = (value: string): string | undefined => {
  const trimmed = value.trim()
  return trimmed !== '' ? trimmed : undefined
}

/**
 * The secret fields the user has re-entered a value into, which are exactly the ones this save
 * rotates. Each is independent: `ManagedIntegrationWriter.replaceMergingCredentials` rewrites the
 * supplied secrets into the stored bundle and keeps the rest, so a Sonar token may be replaced
 * without its webhook secret and a Datadog API key without its application key. Trimmed, so a field
 * holding only whitespace counts as untouched rather than as a rotation.
 */
const getEnteredSecretKeys = (fields: IntegrationField[], values: Record<string, string>): Set<string> =>
  new Set(fields.filter(field => field.isSecret && (values[field.key] ?? '').trim() !== '').map(field => field.key))

/** One read for the field errors and the Save gate, so the two cannot disagree about the set. */
const getSecretKeysMissingFromSet = (fields: IntegrationField[], values: Record<string, string>): string[] => {
  const enteredSecretKeys = getEnteredSecretKeys(fields, values)
  const set = fields.filter(field => field.isRotatedAsSet)
  if (!set.some(field => enteredSecretKeys.has(field.key))) {
    return []
  }
  return set.filter(field => !enteredSecretKeys.has(field.key)).map(field => field.key)
}

export const getSecretSetMessage = (label: string): string =>
  `${label} is replaced together with the other credentials. Enter it too, or leave them all blank to keep the stored ones.`

const PARTIAL_BASIC_AUTH_MESSAGE = 'Basic auth needs both a username and a password. Enter both, or leave both blank.'

/**
 * How far through choosing a webhook authentication method the user is. Unlike a tool's own
 * credentials, basic auth is genuinely one credential in two boxes — a username without a password
 * authenticates nothing — so a half-filled pair is neither a rotation nor a decision to keep the
 * stored one, and `partial` blocks the save and says why instead. These fields sit outside
 * `TOOL_FIELD_CONFIG` because the drawer renders them through a method chooser.
 */
type WebhookRotation = 'none' | 'partial' | 'complete'

const getWebhookRotation = (method: WebhookAuthMethod, values: WebhookValues): WebhookRotation =>
  match(method)
    .with('unchanged', () => 'none' as const)
    .with('secret', () => (values.webhook_secret.trim() !== '' ? ('complete' as const) : ('none' as const)))
    .with('basic', () => {
      const entered = [values.webhook_user, values.webhook_password].filter(value => value.trim() !== '').length
      if (entered === 0) {
        return 'none' as const
      }
      return entered === 2 ? ('complete' as const) : ('partial' as const)
    })
    .exhaustive()

type ManageContext = {
  tool: CreatableIntegrationType | undefined
  fields: IntegrationField[]
  originalName: string
  originalValues: Record<string, string>
  /** The non-secret fields this response actually reported a value for. */
  reportedFieldKeys: ReadonlySet<string>
  /** The non-primary credential sets this instance stores a secret for. */
  storedSecretGroups: ReadonlySet<SecretGroup>
}

/**
 * Which of the instance's optional credential sets hold a stored secret. A secret is never echoed,
 * so this is read from what the response says about it: Sonar reports `webhook_secret_configured`
 * directly, AppScan reports the `webhook_auth_method` that selects its webhook credential, which is
 * present exactly when one is stored, and Azure reports `webhook_configured`. A set the response
 * says nothing about is treated as empty, matching the platform: an instance with no stored webhook
 * secret has none to protect on a move, so `replaceAppScan` lets it move on the key pair alone.
 *
 * GitLab reports nothing, so one that does hold a webhook secret reads as holding none and the
 * drawer lets a move through without asking for it. The platform still refuses that write, since
 * `replacesEveryStoredSecret` asks the stored instance rather than the request, and the refusal
 * surfaces as the endpoint-moved message — a worse report than the field-level one, but the only
 * one available until the response says whether a secret is stored.
 */
const getStoredSecretGroups = (integration: Integration): Set<SecretGroup> => {
  const hasStoredWebhookSecret =
    Reflect.get(integration, 'webhook_secret_configured') === true ||
    Reflect.get(integration, 'webhook_configured') === true ||
    (integration.type === 'appscan' && integration.webhook_auth_method !== undefined)
  return hasStoredWebhookSecret ? new Set<SecretGroup>(['webhook']) : new Set<SecretGroup>()
}

// Recomputed by both the field and footer leaves so neither has to thread it through the form store.
const deriveManageContext = (integration: Integration): ManageContext => {
  const tool = getHasIntegrationSetupForm(integration.type) ? integration.type : undefined
  // Everything downstream — rendering, the changed-field bag, the endpoint-move rule, the secrets a
  // move owes — reads this list.
  const fields = tool ? TOOL_FIELD_CONFIG[tool] : []
  const storedValues = fields.map(field => {
    const stored = field.isSecret ? undefined : getStoredValue(integration, field.key)
    // A select whose stored value the response does not report opens on "Leave unchanged" rather
    // than on an arbitrary option, so the user is never shown a setting Pixee does not know.
    const seeded = stored ?? (field.kind === 'select' ? UNCHANGED_SELECT_VALUE : '')
    return { key: field.key, stored, seeded }
  })
  return {
    tool,
    fields,
    originalName: getInstanceName(integration),
    originalValues: Object.fromEntries(storedValues.map(({ key, seeded }) => [key, seeded])),
    reportedFieldKeys: new Set(storedValues.filter(({ stored }) => stored !== undefined).map(({ key }) => key)),
    storedSecretGroups: getStoredSecretGroups(integration),
  }
}

/**
 * The fields whose value this save changes, ready to spread into the request body. A `null` entry
 * erases what is stored; see `getSentFieldValue`. Each re-entered secret is sent on its own, because
 * the platform merges the supplied secrets into the stored bundle rather than replacing it whole.
 */
const collectChangedFields = ({
  fields,
  values,
  originalValues,
}: {
  fields: IntegrationField[]
  values: Record<string, string>
  originalValues: Record<string, string>
}): Array<readonly [string, string | null]> =>
  fields
    .map(field => {
      const value = values[field.key] ?? ''
      const sent = field.isSecret
        ? secretOrUndefined(value)
        : getSentFieldValue({ field, value, originalValue: originalValues[field.key] })
      return sent === undefined ? undefined : ([field.key, sent] as const)
    })
    .filter((entry): entry is readonly [string, string | null] => entry !== undefined)

/** The endpoint-deciding fields the user has actually moved, which are the ones the helper explains. */
const getMovedEndpointFieldKeys = (context: ManageContext, values: Record<string, string>): string[] =>
  context.fields
    .filter(field => getHasEndpointMoved({ fields: [field], values, originalValues: context.originalValues }))
    .map(field => field.key)

/**
 * Whether this save has to carry fresh credentials, and which ones. A moved endpoint has to be
 * probed with credentials the user just supplied, so the platform refuses a move whose bundle still
 * holds a stored secret. Empty when nothing moved, in which case every secret is optional and each
 * one entered rotates on its own.
 */
const getSecretKeysOwedForMove = (context: ManageContext, values: Record<string, string>): string[] =>
  getHasEndpointMoved({ fields: context.fields, values, originalValues: context.originalValues })
    ? getSecretKeysRequiredForMove({ fields: context.fields, storedSecretGroups: context.storedSecretGroups })
    : []

/**
 * Whether this save also has to rotate AppScan's webhook credential, which is the one credential the
 * move rule cannot reach through `TOOL_FIELD_CONFIG` — the drawer renders it through a method
 * chooser instead, so `getSecretKeysOwedForMove` never names it.
 *
 * Owed only when the instance actually stores one: its response reports `webhook_auth_method`, and
 * `replaceAppScan` lets an instance that sends Pixee no deliveries move on the key pair alone.
 * Withholding "Leave unchanged" from such an instance forced the user to invent a webhook just to
 * correct a server URL.
 *
 * One predicate, because the fieldset that disables "Leave unchanged" and the footer that holds Save
 * both ask it, and two spellings of it had already drifted — the field leaf's was missing the
 * AppScan test the footer's had.
 */
const getIsWebhookRotationOwedForMove = (context: ManageContext, values: Record<string, string>): boolean =>
  context.tool === 'appscan' &&
  context.storedSecretGroups.has('webhook') &&
  getSecretKeysOwedForMove(context, values).length > 0

/**
 * Whether every non-secret field the form insists on still has a value, Name included. Secrets are
 * excluded: they render empty on this drawer by design, and whether they must be filled is decided
 * by the endpoint-move rule instead. What this catches is a stored value the user cleared on a field
 * that has to have one.
 */
const getAreRequiredFieldsFilled = ({
  context,
  values,
  name,
}: {
  context: ManageContext
  values: Record<string, string>
  name: string
}): boolean =>
  name.trim() !== '' &&
  context.fields.every(
    field =>
      field.isSecret ||
      !getIsFieldRequired(field) ||
      // A field the response never reported opens blank for everyone, so a blank one is not something
      // the user cleared and holding the save on it would make the drawer unusable.
      !context.reportedFieldKeys.has(field.key) ||
      (values[field.key] ?? '').trim() !== ''
  )

// The webhook credentials the chooser rotates. Normalized to one optional-keyed shape so each tool's
// body can name the subset its own contract declares; `secretOrUndefined` has already dropped blanks.
// Distinct from `WebhookRotation` above, which is how far through the chooser the user is.
type WebhookRotationBody = {
  webhook_secret?: string
  webhook_user?: string
  webhook_password?: string
}

/**
 * The PUT body for a tool the spec accepts an update for.
 *
 * Built per tool rather than by spreading the changed-field bag: each Update variant declares its own
 * fields, and a spread of `Record<string, string>` satisfies none of them — it type-checks against
 * nothing and carries whatever keys the field config happens to hold. Naming the fields is what makes
 * a tool whose contract gains a field fail here rather than drop it silently.
 *
 * PUT merges rather than replaces, so an omitted field keeps what is stored. Every field is therefore
 * sent only when the user supplied it: `base_uri` never blank, which the endpoint rejects, and
 * credentials only when re-entered to rotate.
 *
 * Every tool that has an endpoint sends it the same way, through `sentBaseUri`. Veracode briefly did
 * not, on the reading that its endpoint was optional where Datadog's and Sonar's were required — it
 * is `.optional()` on all seven update requests, so there was no rule to split them on.
 */
const buildUpdateBody = ({
  tool,
  name,
  sentBaseUri,
  changed,
  webhook,
  webhookMethod,
  webhookRotation,
}: {
  tool: WritableIntegrationType
  name: string
  sentBaseUri: string | undefined
  changed: Record<string, string | null | undefined>
  webhook: WebhookRotationBody
  webhookMethod: WebhookAuthMethod
  webhookRotation: WebhookRotation
}): PutIntegrationVariables['body'] =>
  match(tool)
    .with('datadog', type => ({
      type,
      name,
      base_uri: sentBaseUri,
      api_key: changed.api_key ?? undefined,
      application_key: changed.application_key ?? undefined,
    }))
    .with('arnica', type => ({
      type,
      name,
      base_uri: sentBaseUri,
      api_key: changed.api_key ?? undefined,
    }))
    // `organization` and `github_app_name` are the two fields a Sonar save may erase, so they pass
    // `changed`'s value through rather than discarding its `null` with `??`. Its two secrets rotate
    // independently and both come from `changed`, not from `webhook`: that chooser exists for
    // AppScan, which picks between a shared secret and a basic-auth pair. Sonar has no such choice.
    .with('sonar', type => ({
      type,
      name,
      base_uri: sentBaseUri,
      token: changed.token ?? undefined,
      organization: changed.organization,
      github_app_name: changed.github_app_name,
      webhook_secret: changed.webhook_secret ?? undefined,
    }))
    .with('polaris', type => ({
      type,
      name,
      base_uri: sentBaseUri,
      api_token: changed.api_token ?? undefined,
    }))
    .with('black-duck-hub', type => ({
      type,
      name,
      base_uri: sentBaseUri,
      api_token: changed.api_token ?? undefined,
    }))
    .with('checkmarx-enterprise', type => ({
      type,
      name,
      base_uri: sentBaseUri,
      username: changed.username ?? undefined,
      password: changed.password ?? undefined,
      client_id: changed.client_id ?? undefined,
      client_secret: changed.client_secret ?? undefined,
    }))
    .with('veracode', type => ({
      type,
      name,
      base_uri: sentBaseUri,
      api_key_id: changed.api_key_id ?? undefined,
      api_key_secret: changed.api_key_secret ?? undefined,
      // Three-state: clearing it is how an instance moved back off a proxy stops signing for the
      // old target.
      signing_target: changed.signing_target,
    }))
    // Checkmarx names its hosts by region rather than by URL, so there is no `base_uri` to send.
    .with('checkmarx', type => ({
      type,
      name,
      region: toCheckmarxRegionOrUndefined(changed.region),
      tenant: changed.tenant ?? undefined,
      api_key: changed.api_key ?? undefined,
    }))
    .with('appscan', type => ({
      type,
      name,
      base_uri: sentBaseUri,
      api_key_id: changed.api_key_id ?? undefined,
      api_key_secret: changed.api_key_secret ?? undefined,
      // Keyed off the rotation being *complete*, not off which radio is selected. The platform
      // rejects a request naming a method without that method's credential, and selecting a radio
      // without filling its field is not a rotation — `getWebhookRotation` calls that `none`, and
      // the save is not held for it, so keying off the radio sent a method with nothing to rotate
      // and earned a 400 with no field to anchor it to.
      webhook_auth_method:
        webhookMethod === 'unchanged' || webhookRotation !== 'complete'
          ? undefined
          : getWebhookAuthMethod(webhookMethod),
      ...webhook,
    }))
    // The source providers' secrets are independent of one another, so each changed one is sent on
    // its own rather than as a set the way Datadog's pair is. The exception is moving the server
    // address, which the platform refuses unless every secret comes with it — and refuses outright
    // once repositories have been onboarded through the connection.
    .with('gitlab', type => ({
      type,
      name,
      base_uri: sentBaseUri,
      token: changed.token ?? undefined,
      // Three-state: a connection that stops receiving deliveries clears its secret rather than
      // keeping one nothing checks against.
      webhook_secret: changed.webhook_secret,
    }))
    // No endpoint: Bitbucket Cloud has one host, and a self-hosted Bitbucket is Data Center, a
    // different product with its own integration.
    .with('bitbucket', type => ({
      type,
      name,
      username: changed.username ?? undefined,
      email_address: changed.email_address ?? undefined,
      api_token: changed.api_token ?? undefined,
    }))
    .with('azure', type => ({
      type,
      name,
      base_uri: sentBaseUri,
      organization: changed.organization ?? undefined,
      token: changed.token ?? undefined,
      work_item_id: parseWorkItemIdUpdate(changed.work_item_id),
      commit_message_prefix: changed.commit_message_prefix,
      webhook_password: changed.webhook_password,
    }))
    .with('github', type => ({
      type,
      name,
      base_uri: sentBaseUri,
      app_id: changed.app_id ?? undefined,
      app_name: changed.app_name ?? undefined,
      private_key: changed.private_key ?? undefined,
      webhook_secret: changed.webhook_secret ?? undefined,
    }))
    .exhaustive()

// #endregion

// Secrets are write-only: the key fields render empty and are only sent when re-entered to rotate.
/**
 * The origin a provider outside this browser would reach Pixee at.
 *
 * <p>Read straight from the document rather than through React Router, which reports the in-app
 * path and not the scheme and host a GitLab server has to be told. The lint rule this waives exists
 * to stop navigation being done by hand; {@code getEnvironmentData} waives it for the same kind of
 * read.
 */
const appOrigin = () =>
  // eslint-disable-next-line no-restricted-properties -- an absolute URL for a third party to call, not navigation
  window.location.origin

/**
 * The URL this connection's provider should deliver webhooks to, if it delivers any.
 *
 * Per-connection because the path is what tells Pixee which connection sent a delivery: a payload
 * names a project or an installation, not the server it came from, and the secret the delivery is
 * verified against belongs to one connection. Shown here because the setup steps tell the user to
 * paste it into their provider, and until now nothing showed it.
 *
 * Composed here rather than followed, which is the opposite of what `ManageWebhookUrlSection` does
 * a few lines down. That is not a preference: Sonar and AppScan serve a `_links.webhooks`, and
 * these three serve none, so there is no link to follow. Nothing in this repo verifies the address
 * below is the one the platform listens on, and if the route moves this keeps printing the old one.
 * ISS-8994 asks for the relation; once it exists this component is deleted and
 * `ManageWebhookUrlSection` covers all five.
 */
const WebhookUrl: React.FC<{ integration: Integration }> = ({ integration }) => {
  // Each route names its provider before the connection id. Without that segment the templates are
  // identical, and Jersey merges same-template resources, so one provider's deliveries reach
  // another's handler. The platform also exempts webhook paths from its browser login by matching
  // patterns that end in `webhooks`, so a route shaped any other way answers a delivery with a 302
  // to the identity provider.
  //
  // Which providers those are is read from `getHasConnectionWebhookRoute` rather than re-listed
  // here, so this and the delete confirmation's webhook warning cannot disagree about it. Bitbucket
  // and the tools are outside that list: rendering an address nothing listens on would be worse than
  // rendering none.
  const { type } = integration
  if (!getHasConnectionWebhookRoute(type)) {
    return null
  }

  const path = buildPath('/api/v1/integrations/:type/:integrationId/webhooks', {
    type,
    integrationId: integration.id,
  })

  const url = `${appOrigin()}${path}`

  return (
    <div className={styles.webhookUrl}>
      {/* Disabled rather than merely read-only: the value is derived from the connection's id and
          there is nothing to type here. The copy button is how it is taken. */}
      <TextInput id="manage-webhook-url" label="Webhook URL" value={url} disabled />
      <CopyButton copyText={url} label="Copy webhook URL" tooltipText="Copy" />
    </div>
  )
}

export const ManageDrawer: React.FC<ManageDrawerProps> = ({ integration, onClose }) => {
  const health = getInstanceHealth(integration)
  invariant(integration.id, 'A configured integration always has an id')
  const { originalName, originalValues } = deriveManageContext(integration)
  // The webhook username is seeded from the response because it is not a secret and AppScan echoes
  // it back. Rotating a basic-auth credential replaces the pair, and the platform requires both
  // halves on the same request, so without this the user has to retype from memory a username a
  // typo would silently rewrite. The password stays empty and the method stays on "Leave unchanged":
  // seeding the value is not the same as asking for a rotation.
  const form = useManageForm({
    name: originalName,
    values: originalValues,
    webhookValues: {
      webhook_secret: '',
      webhook_user: (integration.type === 'appscan' ? integration.webhook_user : undefined) ?? '',
      webhook_password: '',
    },
  })
  const drawerContentRef = useRef<HTMLDivElement>(null)

  const toolLabel = getProviderLabel(integration.type)
  const toolUrl = getLinkableToolUrl(integration)

  return (
    <Dialog.Root open modal={false} onOpenChange={open => (!open ? onClose() : undefined)}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <form.AppForm>
          <Dialog.Content
            ref={drawerContentRef}
            className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
            data-has-floating-footer={true}
            onInteractOutside={event => event.preventDefault()}
          >
            <div className={styles.header}>
              <div className={styles.titleRow}>
                <IntegrationBrandIcon integration={integration} />
                <div className={styles.titleText}>
                  <div className={styles.titleNameRow}>
                    <Dialog.Title className={styles.title}>{toolLabel}</Dialog.Title>
                    <HealthDot health={health} failureDetail={getInstanceFailureDetail(integration)} />
                  </div>
                  {toolUrl && (
                    <a className={styles.viewInToolLink} href={toolUrl} target="_blank" rel="noopener noreferrer">
                      View in {toolLabel}
                      <ArrowSquareOutIcon size={16} weight="regular" className={styles.viewInToolIcon} />
                    </a>
                  )}
                </div>
              </div>
              <Dialog.Close asChild>
                <button type="button" className={styles.closeButton} aria-label="Close">
                  <XIcon size={24} weight="regular" />
                </button>
              </Dialog.Close>
            </div>

            <ManageFields integration={integration} />

            <VisuallyHidden>
              <Dialog.Description>Manage the {getInstanceName(integration)} integration.</Dialog.Description>
            </VisuallyHidden>
          </Dialog.Content>

          <FloatingFooter drawerContentRef={drawerContentRef} width="standard">
            <ManageFooter integration={integration} onClose={onClose} />
          </FloatingFooter>
        </form.AppForm>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// #region Field leaves

const ManageFields: React.FC<{ integration: Integration }> = ({ integration }) => {
  const form = useFormContext()
  const context = useMemo(() => deriveManageContext(integration), [integration])
  const { tool, fields, originalValues } = context
  const name = useSelector(form.store, state => state.values.name)
  const values = useSelector(form.store, state => state.values.values)
  const saveError = useSelector(form.store, state => state.values.saveError)

  const secretKeysOwedForMove = new Set(getSecretKeysOwedForMove(context, values))
  const secretKeysMissingFromSet = new Set(getSecretKeysMissingFromSet(fields, values))
  const requiresFreshCredentials = secretKeysOwedForMove.size > 0
  const isWebhookRotationOwedForMove = getIsWebhookRotationOwedForMove(context, values)
  const movedFieldKeys = getMovedEndpointFieldKeys(context, values)
  // Set once repositories have been onboarded through this connection, which fixes the endpoint
  // fields the platform would refuse to move. Closing them off says so before the user types an
  // address that cannot be saved; the refusal itself is still handled, because this count is a
  // snapshot taken when the drawer opened.
  const fixedEndpointReason = getFixedEndpointReason(integration)

  const setValue = (key: string, value: string) =>
    form.setFieldValue('values', previous => ({ ...previous, [key]: value }))

  const mainFields = fields.filter(field => !field.isAdvanced)
  const advancedFields = fields.filter(field => field.isAdvanced)
  const advancedSummary = tool ? ADVANCED_SECTION_SUMMARY[tool] : undefined
  // Opens itself when the section holds something the user needs to see: a value this instance
  // already stores, or an error anchored to a field inside it.
  const hasAdvancedContent = advancedFields.some(
    field => (originalValues[field.key] ?? '') !== '' || getIsFieldAnchored(saveError, field.key)
  )
  // A malformed value disables the save, so the section holding it stays open: collapsed, it would
  // leave a disabled button with nothing on screen explaining why.
  const hasAdvancedFormatError = advancedFields.some(
    field => getFieldFormatError(field, values[field.key]) !== undefined
  )

  const getSecretPlaceholder = (field: IntegrationField): string =>
    secretKeysOwedForMove.has(field.key) ? MOVED_ENDPOINT_PLACEHOLDER : ROTATE_PLACEHOLDER

  const getFieldError = (field: IntegrationField): string | undefined => {
    const anchoredError = getFieldErrorText(saveError, field.key)
    if (anchoredError) {
      return anchoredError
    }
    if (secretKeysMissingFromSet.has(field.key)) {
      return getSecretSetMessage(field.label)
    }
    // A required value the user cleared holds the save, so the field says so rather than leaving a
    // disabled button unexplained. Only for a value this instance actually stores — a field the
    // response never reported is blank for everyone and is not something the user removed.
    if (
      !field.isSecret &&
      getIsFieldRequired(field) &&
      context.reportedFieldKeys.has(field.key) &&
      (values[field.key] ?? '').trim() === ''
    ) {
      return `${field.label} is required.`
    }
    return undefined
  }

  const getIsFieldFixed = (field: IntegrationField): boolean =>
    fixedEndpointReason !== undefined && field.isEndpointFixedOnceOnboarded === true

  // A field that cannot be edited explains why, in place of guidance for editing it — checked first
  // because a fixed field cannot have moved. A field that moved explains the rotation it just
  // demanded; otherwise a field carrying update-specific copy uses it, and everything else falls
  // through to its own helper text.
  const getFieldHelperText = (field: IntegrationField): string | undefined => {
    if (getIsFieldFixed(field)) {
      return fixedEndpointReason
    }
    if (requiresFreshCredentials && movedFieldKeys.includes(field.key)) {
      return MOVED_ENDPOINT_HELPER_TEXT
    }
    return field.manageHelperText
  }

  const renderField = (field: IntegrationField) => (
    <IntegrationFieldInput
      key={field.key}
      field={field}
      id={`manage-${field.key}`}
      value={values[field.key] ?? ''}
      onChange={value => setValue(field.key, value)}
      isRequired={field.isSecret ? secretKeysOwedForMove.has(field.key) : getIsFieldRequired(field)}
      // A select whose stored value the response does not report offers "Leave unchanged", so a user
      // who picked an option — and thereby demanded a full credential rotation — can undo it.
      options={
        field.kind === 'select' && !context.reportedFieldKeys.has(field.key)
          ? [UNCHANGED_SELECT_OPTION, ...(field.options ?? [])]
          : undefined
      }
      placeholder={field.isSecret ? getSecretPlaceholder(field) : undefined}
      helperText={getFieldHelperText(field)}
      errorText={getFieldError(field)}
      isError={getIsFieldAnchored(saveError, field.key)}
      isDisabled={getIsFieldFixed(field)}
    />
  )

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionHeading}>Manage your instance</h3>
      {saveError?.formMessage && <FormErrorBanner message={saveError.formMessage} />}
      <div className={styles.fields}>
        <WebhookUrl integration={integration} />
        {/* Required: an instance is identified by its name everywhere it is listed, so it may be
            renamed but not blanked. */}
        <TextInput
          id="manage-name"
          label="Name"
          isRequired
          value={name}
          onChange={value => form.setFieldValue('name', value)}
          placeholder="Name this integration"
          errorText={getFieldErrorText(saveError, NAME_FIELD_KEY)}
          isError={getIsFieldAnchored(saveError, NAME_FIELD_KEY)}
        />
        {mainFields.map(renderField)}
        {integration.type === 'appscan' && (
          <ManageWebhookAuthFieldset
            integration={integration}
            isLeaveUnchangedDisabled={isWebhookRotationOwedForMove}
          />
        )}
        {advancedSummary && advancedFields.length > 0 && (
          <Disclosure summary={advancedSummary} defaultOpen={hasAdvancedContent} isForcedOpen={hasAdvancedFormatError}>
            {advancedFields.map(renderField)}
          </Disclosure>
        )}
        {tool && getHasWebhookHandoff(tool) && <ManageWebhookUrlSection integration={integration} type={tool} />}
      </div>
    </div>
  )
}

/**
 * The instance's webhook URL, so a customer who skipped or lost it can find it again without
 * recreating the instance. Renders nothing until the platform reports the link — the client never
 * composes the URL from its own origin.
 *
 * `SonarIntegrationLinks` and `AppScanIntegrationLinks` each require a `webhooks` member, so a
 * parsed `Integration` of either type carries the link and this section is live. The `undefined`
 * guard below still earns its place: `getWebhookUrl` reads structurally so it can also take a create
 * response, which nothing has parsed.
 */
const ManageWebhookUrlSection: React.FC<{ integration: Integration; type: WebhookIntegrationType }> = ({
  integration,
  type,
}) => {
  const webhookUrl = getWebhookUrl(integration)
  if (webhookUrl === undefined) {
    return undefined
  }

  // Sonar's response reports whether a secret is stored, and ISS-8743 makes that the same flag that
  // decides enforcement: deliveries are verified when it is true and accepted unverified when it is
  // false. So the warning is shown from what the instance reports, never inferred from the form.
  const isWebhookUnprotected = match(type)
    .with('sonar', () => Reflect.get(integration, 'webhook_secret_configured') !== true)
    // AppScan always authenticates its webhook, so it has no unprotected state to warn about.
    .with('appscan', () => false)
    .exhaustive()

  return (
    <CopyableTextField
      id="manage-webhook-url"
      label={WEBHOOK_URL_LABEL}
      value={webhookUrl}
      copyLabel={WEBHOOK_URL_COPY_LABEL}
      helperText={getWebhookUrlHelperText(getProviderLabel(integration.type))}
      secondaryHelperText={isWebhookUnprotected ? UNPROTECTED_WEBHOOK_HELPER_TEXT : undefined}
    />
  )
}

/**
 * What the instance reports it authenticates its webhook deliveries with, or `undefined` when it
 * reports no method — which is how an instance that sends none reads.
 *
 * Stated rather than preselected on the chooser below. The chooser is a *rotation* control: naming a
 * method asks the platform to replace that method's credential, and the credential is write-only, so
 * preselecting the stored method would make every AppScan save demand a webhook credential the user
 * did not come here to change.
 */
const getStoredWebhookAuthSummary = ({ webhook_auth_method, webhook_user }: AppScanIntegration): string | undefined =>
  match(webhook_auth_method)
    .with('shared-secret', () => 'This instance signs its webhook deliveries with a shared secret.')
    .with('basic-auth', () =>
      webhook_user === undefined
        ? 'This instance authenticates its webhook deliveries with basic auth.'
        : `This instance authenticates its webhook deliveries with basic auth, as ${webhook_user}.`
    )
    .with(undefined, () => undefined)
    .exhaustive()

// The AppScan webhook-auth rotation chooser. "Leave unchanged" keeps the stored credentials; the
// other two options each rotate exactly one method (secret OR the basic-auth pair), mirroring the
// platform's exclusive-OR rule.
const ManageWebhookAuthFieldset: React.FC<{ integration: AppScanIntegration; isLeaveUnchangedDisabled: boolean }> = ({
  integration,
  isLeaveUnchangedDisabled,
}) => {
  const form = useFormContext()
  const method = useSelector(form.store, state => state.values.webhookMethod)
  const webhookValues = useSelector(form.store, state => state.values.webhookValues)
  const isBasicPairPartial = getWebhookRotation(method, webhookValues) === 'partial'
  const storedAuthSummary = getStoredWebhookAuthSummary(integration)
  const setWebhookValue = (key: string, value: string) =>
    form.setFieldValue('webhookValues', previous => ({ ...previous, [key]: value }))

  return (
    <fieldset className={styles.webhookAuthFieldset}>
      <legend className={styles.sectionHeading}>Webhook authentication</legend>
      {storedAuthSummary && <p className={styles.webhookAuthLegend}>{storedAuthSummary}</p>}
      {isLeaveUnchangedDisabled && <p className={styles.webhookAuthLegend}>{MOVED_ENDPOINT_HELPER_TEXT}</p>}
      <RadioGroup
        className={styles.webhookAuthOptions}
        value={method}
        onValueChange={value =>
          form.setFieldValue(
            'webhookMethod',
            match(value)
              .with('basic', () => 'basic' as const)
              .with('secret', () => 'secret' as const)
              .otherwise(() => 'unchanged' as const)
          )
        }
      >
        <RadioItem
          id="manage-webhook-method-unchanged"
          value="unchanged"
          label="Leave unchanged"
          disabled={isLeaveUnchangedDisabled}
        />
        <RadioItem id="manage-webhook-method-secret" value="secret" label="Shared secret" />
        <RadioItem id="manage-webhook-method-basic" value="basic" label="Basic auth" />
      </RadioGroup>
      {match(method)
        .with('secret', () => (
          <TextInput
            id="manage-webhook_secret"
            label="Webhook secret"
            type="password"
            value={webhookValues.webhook_secret}
            onChange={value => setWebhookValue('webhook_secret', value)}
            placeholder={isLeaveUnchangedDisabled ? MOVED_ENDPOINT_PLACEHOLDER : ROTATE_PLACEHOLDER}
          />
        ))
        .with('basic', () => (
          <>
            <TextInput
              id="manage-webhook_user"
              label="Webhook username"
              value={webhookValues.webhook_user}
              onChange={value => setWebhookValue('webhook_user', value)}
              errorText={
                isBasicPairPartial && webhookValues.webhook_user.trim() === '' ? PARTIAL_BASIC_AUTH_MESSAGE : undefined
              }
            />
            <TextInput
              id="manage-webhook_password"
              label="Webhook password"
              type="password"
              value={webhookValues.webhook_password}
              onChange={value => setWebhookValue('webhook_password', value)}
              placeholder={isLeaveUnchangedDisabled ? MOVED_ENDPOINT_PLACEHOLDER : ROTATE_PLACEHOLDER}
              errorText={
                isBasicPairPartial && webhookValues.webhook_password.trim() === ''
                  ? PARTIAL_BASIC_AUTH_MESSAGE
                  : undefined
              }
            />
          </>
        ))
        .with('unchanged', () => undefined)
        .exhaustive()}
    </fieldset>
  )
}

// #endregion

// #region Footer leaf

type ManageFooterProps = { integration: Integration; onClose: () => unknown }

const ManageFooter: React.FC<ManageFooterProps> = ({ integration, onClose }) => {
  const form = useFormContext()
  const context = useMemo(() => deriveManageContext(integration), [integration])
  const { tool, fields, originalName, originalValues } = context
  const values = useSelector(form.store, state => state.values.values)
  const name = useSelector(form.store, state => state.values.name)
  const webhookMethod = useSelector(form.store, state => state.values.webhookMethod)
  const webhookValues = useSelector(form.store, state => state.values.webhookValues)

  const { addToast } = useToastService()
  const queryClient = useQueryClient()
  const [isDocsOpen, setIsDocsOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const docsEntry = getCatalogEntryForType(integration.type)

  // No `409` override: one keyed on the status alone cannot tell the conflicts apart, and the
  // shipped override asserted an optimistic-lock conflict for every one of them — which is the bug
  // this fixes, not a copy to preserve. A conflict the form explains is not toasted at all (see the
  // early return below); one it cannot identify falls through to here, where the platform's own
  // title and detail say what happened without the client guessing.
  const showSaveError = useShowMutationError({ prefix: 'Failed to save integration' })
  const putIntegration = usePutIntegration({
    onSuccess: () => {
      addToast({ message: <>{getInstanceName(integration)} updated.</>, variant: 'success' })
      onClose()
    },
    onError: (...args) => {
      const [error] = args
      const saveError = getIntegrationSaveError({
        error,
        fields,
        values: form.state.values.values,
        toolLabel: getProviderLabel(integration.type),
        operation: 'update',
      })
      form.setFieldValue('saveError', saveError)
      if (getIsEndpointFixedRefusal(error)) {
        // Reaching this means the drawer did not close the field off, so the count it opened with
        // was stale or absent. Refreshes the instance cards behind it; the drawer itself holds its
        // instance as a snapshot, so the field stays open until it is reopened — which is why the
        // refusal is reported on the field rather than left to the affordance.
        queryClient.invalidateQueries({ queryKey: ['integrations'] })
      }
      if (getIsConflictReportedOnField(error, saveError)) {
        return
      }
      showSaveError(...args)
    },
  })

  const isBusy = putIntegration.isPending
  const deleteBlockedReason = getDeleteBlockedReason(integration)

  const trimmedName = name.trim()
  const isNameChanged = trimmedName !== '' && trimmedName !== originalName
  const changedFields = collectChangedFields({ fields, values, originalValues })
  const enteredSecretKeys = getEnteredSecretKeys(fields, values)
  const webhookRotation = getWebhookRotation(webhookMethod, webhookValues)
  const secretKeysOwedForMove = getSecretKeysOwedForMove(context, values)

  const hasChanges = isNameChanged || changedFields.length > 0 || webhookRotation !== 'none'
  // A required field the user cleared holds the save. Held here rather than silently falling back,
  // as the Setup drawer holds its own.
  const areRequiredFieldsFilled = getAreRequiredFieldsFilled({ context, values, name })
  // A save that moves the endpoint is only offered once every credential the platform would probe
  // with has been supplied, which pre-empts its refusal rather than reporting it. AppScan's webhook
  // credential is held to the same rule by `getIsWebhookRotationOwedForMove`, which is also what
  // disables "Leave unchanged" on the chooser above.
  const isWebhookRotationOwedForMove = getIsWebhookRotationOwedForMove(context, values)
  const areFreshCredentialsSupplied =
    secretKeysOwedForMove.every(key => enteredSecretKeys.has(key)) &&
    (!isWebhookRotationOwedForMove || webhookRotation === 'complete')
  const areFieldFormatsValid = fields.every(field => getFieldFormatError(field, values[field.key]) === undefined)
  const isSecretSetComplete = getSecretKeysMissingFromSet(fields, values).length === 0
  const isSavable =
    hasChanges &&
    areRequiredFieldsFilled &&
    webhookRotation !== 'partial' &&
    isSecretSetComplete &&
    areFreshCredentialsSupplied &&
    areFieldFormatsValid

  const handleSave = () => {
    // Only a tool with a field config can build a body. `toConfiguredInstances` filters on `source`,
    // not type, so an SCM integration marked `managed` would arrive here with no config — and the
    // drawer would look editable while Save did nothing. Fail loudly instead of silently.
    invariant(tool !== undefined, `ManageDrawer opened for ${integration.type}, which has no editable fields`)
    // The AppScan webhook-auth chooser's keys, which sit outside `TOOL_FIELD_CONFIG` because the
    // drawer renders them through a method chooser rather than as ordinary fields.
    //
    // The `unchanged` arm contributes no keys at all, and that is load-bearing rather than tidy: this
    // object is spread over `changed` below, and Sonar's webhook secret *is* an ordinary field in its
    // own config, so it arrives through `changed`. Rewriting these arms to name all three keys with
    // an `undefined` value — which would otherwise be the idiom this repo prefers — makes every
    // non-AppScan save overwrite `changed.webhook_secret` with `undefined` and silently stop
    // rotating Sonar's webhook secret.
    const webhook = match(webhookMethod)
      .with('secret', () => ({ webhook_secret: secretOrUndefined(webhookValues.webhook_secret) }))
      .with('basic', () => ({
        webhook_user: secretOrUndefined(webhookValues.webhook_user),
        webhook_password: secretOrUndefined(webhookValues.webhook_password),
      }))
      .with('unchanged', () => ({}))
      .exhaustive()
    const changed = Object.fromEntries(changedFields)
    // PUT merges rather than replaces: a field carrying a value replaces it, an omitted field keeps
    // what is stored, and an explicit `null` erases it. The endpoint is sent whenever it is known —
    // never blank, which the endpoint rejects. Re-sending an unedited one changes nothing, and the
    // origins compare equal so it is not read as a move.
    //
    // `??` already discards the `null` that erases a field, so this is `string | undefined`. No
    // writable tool can erase its endpoint anyway: it is required on every one of them.
    const baseUri = changed.base_uri ?? originalValues.base_uri
    const sentBaseUri = baseUri === '' ? undefined : baseUri
    const name = isNameChanged ? trimmedName : originalName
    // A type the spec accepts a body for is spelled out per tool, because `usePutIntegration` parses
    // it against that tool's schema and a generic bag of keys would not satisfy it. One arm per
    // writable type, the way `buildCreateBody` does it — a spec bump that widens the union fails
    // `exhaustive()` here rather than silently posting the previous tool's shape. That is not
    // hypothetical: this arm was Datadog's four fields alone, and when the Sonar contract landed on
    // main every Sonar save started dropping its token, webhook secret, organization and GitHub App
    // name on the floor while reporting success.
    // Every type with a setup form is also one the spec accepts an update for, so this holds today.
    // Asserted rather than given a spread-the-bag fallback: `usePutIntegration` parses the body
    // against `UpdateIntegrationRequestSchema`, so a type outside that union has no shape a fallback
    // could build — it would throw inside the mutation with nothing naming a field. Failing here
    // names the type instead, and a spec bump that drops a write contract is a decision to make
    // rather than a body to improvise.
    invariant(
      getIsIntegrationTypeSupportedByApi(tool),
      `${tool} has a setup form but no write contract in the spec, so its update body cannot be built`
    )
    const body: PutIntegrationVariables['body'] = buildUpdateBody({
      tool,
      name,
      sentBaseUri,
      changed,
      webhook,
      webhookMethod,
      webhookRotation,
    })
    invariant(integration.id, 'A configured integration always has an id')
    form.setFieldValue('saveError', undefined)
    putIntegration.mutate({ integrationId: integration.id, body })
  }

  return (
    <>
      {/* Saving and deleting are mutually exclusive on the same instance, so a save in flight rules
          delete out until it settles. */}
      <DeleteIntegrationButton
        blockedReason={deleteBlockedReason}
        isBusy={isBusy}
        onDelete={() => setIsDeleteModalOpen(true)}
      />
      <div className={styles.footerRightGroup}>
        {docsEntry && (
          <TooltipWithHover
            trigger={
              <button
                type="button"
                className={styles.docsIconButton}
                onClick={() => setIsDocsOpen(prev => !prev)}
                aria-label="View documentation"
              >
                <QuestionIcon size={20} weight="regular" />
              </button>
            }
          >
            View documentation
          </TooltipWithHover>
        )}
        <GhostButton type="secondary" state={isBusy ? 'disabled' : 'default'} onClick={onClose}>
          Cancel
        </GhostButton>
        <DefaultButton
          buttonType="primary"
          state={isBusy ? 'loading' : isSavable ? 'default' : 'disabled'}
          onClick={handleSave}
          loadingLabel="Saving"
        >
          Test and save changes
        </DefaultButton>
      </div>
      {isDocsOpen && docsEntry && (
        <DocumentationDrawer entry={docsEntry} placement="beside" onClose={() => setIsDocsOpen(false)} />
      )}
      {isDeleteModalOpen && (
        <DeleteIntegrationModal
          integration={integration}
          onCancel={() => setIsDeleteModalOpen(false)}
          onSuccess={onClose}
        />
      )}
    </>
  )
}

type DeleteIntegrationButtonProps = {
  /** The sentence to show instead of offering Delete, or `undefined` to offer it. */
  blockedReason?: string
  isBusy: boolean
  onDelete: () => unknown
}

/**
 * Delete, closed off while repositories still point at the instance so the user is not sent into a
 * confirmation the platform will refuse with a 409.
 *
 * Blocked by `aria-disabled` rather than `disabled`, because the refusal comes with a reason the user
 * has to be able to reach: a natively disabled button leaves the tab order, so a keyboard user would
 * never trigger the hover the tooltip needs. The reason is also in the accessible name, for anyone
 * reading the button without landing on it. A save in flight has nothing to explain, so it uses the
 * ordinary disabled state.
 */
const DeleteIntegrationButton: React.FC<DeleteIntegrationButtonProps> = ({ blockedReason, isBusy, onDelete }) => {
  const button = (
    <GhostButton
      type="destructive"
      state={isBusy ? 'disabled' : 'default'}
      ariaDisabled={blockedReason !== undefined}
      onClick={onDelete}
      ariaLabel={blockedReason ? `Delete integration. ${blockedReason}` : undefined}
    >
      <span className={styles.iconLabel}>
        <TrashSimpleIcon size={16} weight="regular" />
        Delete integration
      </span>
    </GhostButton>
  )

  if (!blockedReason) {
    return button
  }

  return (
    <TooltipWithHover
      trigger={<span className={styles.footerDeleteTooltipTrigger}>{button}</span>}
      side="top"
      maxWidth={280}
    >
      {blockedReason}
    </TooltipWithHover>
  )
}

// #endregion
